import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Alert,
  Text
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconInfoCircle,
  IconPlus,
  IconDeviceFloppy
} from '@tabler/icons-react';
import { aiKnowledgeApi, AIKnowledgeText } from '@/api/aiKnowledge.api';

interface TextEntryModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editEntry: AIKnowledgeText | null;
}

const TextEntryModal = ({ opened, onClose, onSuccess, editEntry }: TextEntryModalProps) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editEntry) {
      setTitle(editEntry.title);
      setContent(editEntry.content);
      setCategory(editEntry.category);
    } else {
      setTitle('');
      setContent('');
      setCategory(null);
    }
  }, [editEntry, opened]);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.texts.requiredFields'),
        color: 'red'
      });
      return;
    }

    try {
      setSaving(true);

      if (editEntry) {
        const response = await aiKnowledgeApi.updateText(editEntry.id, {
          title: title.trim(),
          content: content.trim(),
          category: category || undefined
        });
        if (response.data.success) {
          notifications.show({
            title: t('common.success'),
            message: t('aiKnowledge.texts.updated'),
            color: 'green',
            icon: <IconCheck size={18} />
          });
        }
      } else {
        const response = await aiKnowledgeApi.createText({
          title: title.trim(),
          content: content.trim(),
          category: category || undefined
        });
        if (response.data.success) {
          notifications.show({
            title: t('common.success'),
            message: t('aiKnowledge.texts.created'),
            color: 'green',
            icon: <IconCheck size={18} />
          });
        }
      }

      onSuccess();
    } catch (error) {
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.errors.saveFailed'),
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    { value: 'faq', label: t('aiKnowledge.texts.categories.faq') },
    { value: 'policies', label: t('aiKnowledge.texts.categories.policies') },
    { value: 'operations', label: t('aiKnowledge.texts.categories.operations') },
    { value: 'pricing', label: t('aiKnowledge.texts.categories.pricing') },
    { value: 'locations', label: t('aiKnowledge.texts.categories.locations') },
    { value: 'services', label: t('aiKnowledge.texts.categories.services') },
    { value: 'other', label: t('aiKnowledge.texts.categories.other') }
  ];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editEntry ? t('aiKnowledge.texts.editTitle') : t('aiKnowledge.texts.createTitle')}
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          label={t('aiKnowledge.texts.titleLabel')}
          placeholder={t('aiKnowledge.texts.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <Select
          label={t('aiKnowledge.texts.categoryLabel')}
          placeholder={t('aiKnowledge.texts.categoryPlaceholder')}
          data={categories}
          value={category}
          onChange={setCategory}
          clearable
        />

        <Textarea
          label={t('aiKnowledge.texts.contentLabel')}
          placeholder={t('aiKnowledge.texts.contentPlaceholder')}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          minRows={8}
          maxRows={20}
          autosize
          required
        />

        <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} p="xs">
          <Text size="xs">{t('aiKnowledge.texts.contentHelp')}</Text>
        </Alert>

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={!title.trim() || !content.trim()}
            leftSection={editEntry ? <IconDeviceFloppy size={16} /> : <IconPlus size={16} />}
            variant="gradient"
            gradient={{ from: 'violet', to: 'blue' }}
          >
            {editEntry ? t('common.save') : t('aiKnowledge.texts.createButton')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default TextEntryModal;
